import React, { useState, useEffect } from "react";
import { ExternalLink, Search } from "lucide-react";
import Badge from "../../../components/common/Badge";
import { statusVariant } from "../../../utils/statusVariant";
import Toast, { ToastMessage } from "../../../components/common/Toast";
import NoDataFound from "../../../components/common/NoDataFound";
import FormControl from "../../../components/common/FormControl";
import Pagination from "../../../components/common/Pagination";
import Skeleton from "../../../components/common/Skeleton";
import { visibleForScope, isClosedMeeting } from "./workArchive";
import { useEntitySettings } from "../../../contexts/EntitySettingsContext";
import "../../../components/layout/ModuleLayout.css";

export interface GovernanceMeeting {
  id: number;
  meeting_code: string;
  meeting_title: string;
  meeting_type: string;
  scheduled_date?: string;
  scheduled_time_from?: string;
  scheduled_time_to?: string;
  meeting_status: string;
  meeting_link?: string | null;
  my_role?: "Chairman" | "Participant";
  chairman_name?: string;
}

interface WorkMeetingsBodyProps {
  meetings: GovernanceMeeting[];
  loading: boolean;
  error: string | null;
  onRowClick: (meeting: GovernanceMeeting) => void;
  /**
   * 'active' (default) → live Action Centre list, unchanged.
   * 'archive' → only CLOSED / CANCELLED / past meetings (History twin).
   */
  scope?: "active" | "archive";
  /**
   * Read-only History mode. Hides every mutating toolbar control while
   * leaving row-click → detail modal intact. Defaults to false so the
   * Action Centre behaves exactly as before.
   */
  readOnly?: boolean;
}

const StatusBadge = ({ status }: { status: string }) => (
  <Badge variant={statusVariant(status)} size="sm">{status}</Badge>
);

const formatTime = (t?: string | null) => {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm} ${ampm}`;
};

const WorkMeetingsBody: React.FC<WorkMeetingsBodyProps> = ({
  meetings,
  loading,
  error,
  onRowClick,
  scope = "active",
  readOnly = false,
}) => {
  const { formatDate } = useEntitySettings();
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (error) setToast({ type: "error", message: error });
  }, [error]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize]);

  // Scope first (active = unchanged, archive = closed/past only), then the
  // existing search filter composes on top of the scoped set.
  const scoped = visibleForScope(meetings, (m) => isClosedMeeting(m), scope);
  const filtered = scoped.filter((m) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      m.meeting_code.toLowerCase().includes(q) ||
      m.meeting_title.toLowerCase().includes(q) ||
      m.meeting_type.toLowerCase().includes(q)
    );
  });

  const totalRecords = filtered.length;
  const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div
      className="netbody-card flex flex-col h-full"
      data-scope={scope}
      data-readonly={readOnly ? "true" : undefined}
    >
      {/* This body has no mutating toolbar control (no Create/edit/delete).
          The page-size + search are view-only, so readOnly leaves them as-is;
          mutating affordances live inside MeetingDetailModal, which receives
          readOnly from the shell. */}
      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.Input
            type="text"
            placeholder="Search…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
          />
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Title</th>
                    <th className="table-list-th">Type</th>
                    <th className="table-list-th">Scheduled</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                    <th className="table-list-th table-list-th--right">Join</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && (
                    <Skeleton.TableRows
                      cols={["2xs", "sm", "md", "sm", "sm", "pill", { width: "icon", align: "right" }]}
                    />
                  )}
                  {!loading && filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="table-list-cell">
                        <NoDataFound
                          title="No Meetings Found"
                          message={
                            scope === "archive"
                              ? "No past meetings."
                              : "No meetings have been assigned to you."
                          }
                        />
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    paginatedRows.map((m, idx) => (
                      <tr
                        key={m.id}
                        data-testid={`BTN_work_meetings_row_${m.id}_open`}
                        className="table-list-row"
                        onClick={() => onRowClick(m)}
                      >
                        <td className="table-list-cell table-list-cell--sno">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="table-list-cell">
                          <code className="text-[11px] text-slate-500 font-medium">
                            {m.meeting_code}
                          </code>
                        </td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title">{m.meeting_title}</span>
                          {m.my_role && (
                            <span className="table-list-cell-sub">{m.my_role}</span>
                          )}
                        </td>
                        <td className="table-list-cell">{m.meeting_type}</td>
                        <td className="table-list-cell">
                          {m.scheduled_date
                            ? formatDate(m.scheduled_date)
                            : "—"}
                          {m.scheduled_time_from && (
                            <div className="table-list-cell-sub">
                              {formatTime(m.scheduled_time_from)}
                              {m.scheduled_time_to
                                ? ` – ${formatTime(m.scheduled_time_to)}`
                                : ""}
                            </div>
                          )}
                        </td>
                        <td className="table-list-cell">
                          <StatusBadge status={m.meeting_status} />
                        </td>
                        <td className="table-list-cell table-list-cell--right">
                          {m.meeting_link ? (
                            <a
                              data-testid={`LNK_work_meetings_row_${m.id}_join`}
                              href={m.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="table-list-action"
                              title="Join meeting"
                            >
                              <ExternalLink size={14} />
                            </a>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  {!loading && filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="table-list-empty-cell">
                        <NoDataFound
                          title="No Meetings Found"
                          message={
                            scope === "archive"
                              ? "No past meetings."
                              : "No meetings have been assigned to you."
                          }
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="meetings"
            />
          </div>
        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default WorkMeetingsBody;
